import React, { useEffect, useState, useMemo } from 'react';
import { equipmentAPI, playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  HardHat, 
  Shield, 
  Shirt, 
  Plus,
  Search,
  CheckCircle,
  XCircle,
  ArrowRightLeft,
  Download,
  Pencil,
  Trash2,
  Package,
  User,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const EQUIPMENT_TYPES = [
  { value: 'helmet', label: 'Helmet', icon: HardHat },
  { value: 'shoulder_pads', label: 'Shoulder Pads', icon: Shield },
  { value: 'uniform', label: 'Uniform', icon: Shirt },
];

// CONDITIONS removed per user request - no longer tracking condition

export default function EquipmentPage() {
  const [equipment, setEquipment] = useState([]);
  const [players, setPlayers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('inventory'); // inventory, checkout, report
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [checkoutModal, setCheckoutModal] = useState({ open: false, item: null });
  
  // Form state
  const [formData, setFormData] = useState({
    equipment_type: 'helmet',
    equipment_number: '',
    size: '',
    notes: '',
    assigned_to: ''  // Player ID to assign to
  });
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [addPlayerSearch, setAddPlayerSearch] = useState('');  // For add modal player search
  
  // Report data
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [equipRes, playersRes, logsRes] = await Promise.all([
        equipmentAPI.getAll(),
        playersAPI.getAll(),
        equipmentAPI.getLogs()
      ]);
      setEquipment(equipRes.data);
      setPlayers(playersRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const res = await equipmentAPI.getSizeReport();
      setReportData(res.data);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  useEffect(() => {
    if (viewMode === 'report' && !reportData) {
      loadReport();
    }
  }, [viewMode, reportData]);

  const filteredEquipment = useMemo(() => {
    return equipment.filter(item => {
      if (filterType !== 'all' && item.equipment_type !== filterType) return false;
      if (filterStatus !== 'all' && item.status !== filterStatus) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.equipment_number.toLowerCase().includes(query) ||
          item.equipment_type.toLowerCase().includes(query) ||
          (item.assigned_to_name && item.assigned_to_name.toLowerCase().includes(query))
        );
      }
      return true;
    });
  }, [equipment, filterType, filterStatus, searchQuery]);

  const filteredPlayers = useMemo(() => {
    if (!playerSearch) return players;
    const query = playerSearch.toLowerCase();
    return players.filter(p => 
      p.name.toLowerCase().includes(query) ||
      (p.jersey_number && p.jersey_number.toString().includes(query))
    );
  }, [players, playerSearch]);

  const filteredPlayersForAdd = useMemo(() => {
    if (!addPlayerSearch) return players;
    const query = addPlayerSearch.toLowerCase();
    return players.filter(p => 
      p.name.toLowerCase().includes(query) ||
      (p.jersey_number && p.jersey_number.toString().includes(query))
    );
  }, [players, addPlayerSearch]);

  const handleAddEquipment = async () => {
    if (!formData.equipment_number) {
      toast.error('Equipment number is required');
      return;
    }
    
    try {
      // Create the equipment first
      const createData = {
        equipment_type: formData.equipment_type,
        equipment_number: formData.equipment_number,
        size: formData.size,
        notes: formData.notes
      };
      const res = await equipmentAPI.create(createData);
      
      // If a player was selected, checkout to them
      if (formData.assigned_to) {
        try {
          await equipmentAPI.checkout(res.data.id, formData.assigned_to);
        } catch (e) {
          console.error('Failed to assign to player:', e);
        }
      }
      
      toast.success('Equipment added successfully');
      setAddModal(false);
      setFormData({ equipment_type: 'helmet', equipment_number: '', size: '', notes: '', assigned_to: '' });
      setAddPlayerSearch('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add equipment');
    }
  };

  const handleEditEquipment = async () => {
    if (!editModal.item) return;
    
    try {
      await equipmentAPI.update(editModal.item.id, formData);
      toast.success('Equipment updated successfully');
      setEditModal({ open: false, item: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update equipment');
    }
  };

  const handleDeleteEquipment = async () => {
    if (!deleteModal.item) return;
    
    try {
      await equipmentAPI.delete(deleteModal.item.id);
      toast.success('Equipment deleted successfully');
      setDeleteModal({ open: false, item: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete equipment');
    }
  };

  const handleCheckout = async () => {
    if (!checkoutModal.item || !selectedPlayerId) {
      toast.error('Please select a player');
      return;
    }
    
    try {
      await equipmentAPI.checkout(checkoutModal.item.id, selectedPlayerId);
      toast.success('Equipment checked out successfully');
      setCheckoutModal({ open: false, item: null });
      setSelectedPlayerId('');
      setPlayerSearch('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkout equipment');
    }
  };

  const handleCheckin = async (item) => {
    try {
      await equipmentAPI.checkin(item.id);
      toast.success('Equipment checked in successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkin equipment');
    }
  };

  const openEditModal = (item) => {
    setFormData({
      equipment_type: item.equipment_type,
      equipment_number: item.equipment_number,
      size: item.size || '',
      notes: item.notes || '',
      assigned_to: item.assigned_to || ''
    });
    setEditModal({ open: true, item });
  };

  const getTypeIcon = (type) => {
    const typeInfo = EQUIPMENT_TYPES.find(t => t.value === type);
    return typeInfo ? typeInfo.icon : Package;
  };

  const getTypeLabel = (type) => {
    const typeInfo = EQUIPMENT_TYPES.find(t => t.value === type);
    return typeInfo ? typeInfo.label : type;
  };

  const exportReport = () => {
    if (!reportData) return;
    
    // Create CSV content
    let csv = 'Player Size Report\n\n';
    csv += 'Player Name,Jersey #,Position,Shirt Size,Short Size,Shoe Size\n';
    
    reportData.players.forEach(p => {
      csv += `${p.name},${p.jersey_number || ''},${p.position || ''},${p.shirt_size || ''},${p.short_size || ''},${p.shoe_size || ''}\n`;
    });
    
    csv += '\n\nEquipment Inventory\n';
    csv += 'Type,Total,Available,Checked Out\n';
    
    Object.entries(reportData.equipment_inventory).forEach(([type, data]) => {
      csv += `${getTypeLabel(type)},${data.total},${data.available},${data.checked_out}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `equipment_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Stats
  const stats = useMemo(() => {
    const total = equipment.length;
    const available = equipment.filter(e => e.status === 'available').length;
    const checkedOut = equipment.filter(e => e.status === 'checked_out').length;
    return { total, available, checkedOut };
  }, [equipment]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="equipment-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-[#71717A] uppercase tracking-wider">Equipment</p>
            <h1 className="font-display text-4xl text-white tracking-tight">EQUIPMENT MANAGEMENT</h1>
          </div>

          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="inventory" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="inventory-tab"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Inventory
                </TabsTrigger>
                <TabsTrigger 
                  value="checkout" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="checkout-tab"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Check In/Out
                </TabsTrigger>
                <TabsTrigger 
                  value="report" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="report-tab"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Size Report
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Button
              onClick={() => setAddModal(true)}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              data-testid="add-equipment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Equipment
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-white">{stats.total}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Total Items</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#32D74B]">{stats.available}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Available</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-4 text-center">
              <p className="font-display text-3xl text-[#007AFF]">{stats.checkedOut}</p>
              <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Checked Out</p>
            </CardContent>
          </Card>
        </div>

        {/* Content based on view mode */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
          </div>
        ) : viewMode === 'inventory' ? (
          /* Inventory View */
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <CardTitle className="text-white flex-1">Equipment Inventory</CardTitle>
                
                {/* Filters */}
                <div className="flex gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white w-48"
                      data-testid="search-input"
                    />
                  </div>
                  
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 text-sm"
                    data-testid="type-filter"
                  >
                    <option value="all">All Types</option>
                    {EQUIPMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 text-sm"
                    data-testid="status-filter"
                  >
                    <option value="all">All Status</option>
                    <option value="available">Available</option>
                    <option value="checked_out">Checked Out</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredEquipment.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Type</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Number</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Size</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Status</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Assigned To</th>
                        <th className="p-4 text-xs text-[#71717A] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEquipment.map((item) => {
                        const TypeIcon = getTypeIcon(item.equipment_type);
                        
                        return (
                          <tr key={item.id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <TypeIcon className="w-5 h-5 text-[#71717A]" />
                                <span className="text-white">{getTypeLabel(item.equipment_type)}</span>
                              </div>
                            </td>
                            <td className="p-4 text-white font-mono">{item.equipment_number}</td>
                            <td className="p-4 text-[#A1A1AA]">{item.size || '-'}</td>
                            <td className="p-4">
                              {item.status === 'available' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#32D74B]/10 text-[#32D74B] text-xs">
                                  <CheckCircle className="w-3 h-3" />
                                  Available
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#007AFF]/10 text-[#007AFF] text-xs">
                                  <XCircle className="w-3 h-3" />
                                  Checked Out
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-[#A1A1AA]">
                              {item.assigned_to_name || '-'}
                            </td>
                            <td className="p-4">
                              <div className="flex gap-2 justify-center">
                                <Button
                                  size="sm"
                                  onClick={() => openEditModal(item)}
                                  className="bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                                  data-testid={`edit-${item.id}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => setDeleteModal({ open: true, item })}
                                  className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                                  data-testid={`delete-${item.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Package className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No equipment found</p>
                  <p className="text-sm">Add equipment to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'checkout' ? (
          /* Check In/Out View */
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="text-white">Check In / Check Out Equipment</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {equipment.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {equipment.map((item) => {
                    const TypeIcon = getTypeIcon(item.equipment_type);
                    
                    return (
                      <div key={item.id} className="flex items-center justify-between p-4 hover:bg-[#1A1A1A]">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[#27272A] flex items-center justify-center">
                            <TypeIcon className="w-6 h-6 text-[#71717A]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">
                              {getTypeLabel(item.equipment_type)} #{item.equipment_number}
                            </p>
                            <p className="text-sm text-[#71717A]">
                              Size: {item.size || 'N/A'}
                            </p>
                            {item.assigned_to_name && (
                              <p className="text-sm text-[#007AFF]">
                                Assigned to: {item.assigned_to_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div>
                          {item.status === 'available' ? (
                            <Button
                              onClick={() => setCheckoutModal({ open: true, item })}
                              className="bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
                              data-testid={`checkout-${item.id}`}
                            >
                              <ArrowRightLeft className="w-4 h-4 mr-2" />
                              Check Out
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleCheckin(item)}
                              className="bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                              data-testid={`checkin-${item.id}`}
                            >
                              <ArrowRightLeft className="w-4 h-4 mr-2" />
                              Check In
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <ArrowRightLeft className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No equipment to check in/out</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Report View */
          <div className="space-y-6">
            <div className="flex justify-end">
              <Button
                onClick={exportReport}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
                disabled={!reportData}
                data-testid="export-report-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {reportData ? (
              <>
                {/* Equipment Summary */}
                <Card className="bg-[#121212] border-[#27272A]">
                  <CardHeader className="border-b border-[#27272A]">
                    <CardTitle className="text-white">Equipment Inventory Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(reportData.equipment_inventory).map(([type, data]) => (
                        <div key={type} className="bg-[#0A0A0A] p-4 border border-[#27272A]">
                          <h4 className="text-white font-medium mb-2">{getTypeLabel(type)}</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-[#A1A1AA]">Total: <span className="text-white">{data.total}</span></p>
                            <p className="text-[#A1A1AA]">Available: <span className="text-[#32D74B]">{data.available}</span></p>
                            <p className="text-[#A1A1AA]">Checked Out: <span className="text-[#007AFF]">{data.checked_out}</span></p>
                            <div className="mt-2 pt-2 border-t border-[#27272A]">
                              <p className="text-xs text-[#71717A] mb-1">By Size:</p>
                              {Object.entries(data.by_size).map(([size, count]) => (
                                <span key={size} className="inline-block mr-2 text-xs text-[#A1A1AA]">
                                  {size}: {count}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      {Object.keys(reportData.equipment_inventory).length === 0 && (
                        <p className="text-[#71717A] col-span-3">No equipment in inventory</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Player Sizes */}
                <Card className="bg-[#121212] border-[#27272A]">
                  <CardHeader className="border-b border-[#27272A]">
                    <CardTitle className="text-white">Player Size Requirements ({reportData.total_players} players)</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#27272A]">
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Player</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Jersey #</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Position</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Shirt Size</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Short Size</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider">Shoe Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.players.map((player) => (
                            <tr key={player.id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                              <td className="p-4 text-white">{player.name}</td>
                              <td className="p-4 text-[#A1A1AA]">{player.jersey_number || '-'}</td>
                              <td className="p-4 text-[#A1A1AA]">{player.position || '-'}</td>
                              <td className="p-4 text-[#A1A1AA]">{player.shirt_size || '-'}</td>
                              <td className="p-4 text-[#A1A1AA]">{player.short_size || '-'}</td>
                              <td className="p-4 text-[#A1A1AA]">{player.shoe_size || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
              </div>
            )}
          </div>
        )}

        {/* Add Equipment Modal */}
        <Dialog open={addModal} onOpenChange={(open) => {
          setAddModal(open);
          if (!open) {
            setFormData({ equipment_type: 'helmet', equipment_number: '', size: '', notes: '', assigned_to: '' });
            setAddPlayerSearch('');
          }
        }}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>Add Equipment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Equipment Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {EQUIPMENT_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, equipment_type: type.value })}
                      className={`p-3 border flex flex-col items-center gap-1 transition-all ${
                        formData.equipment_type === type.value 
                          ? 'border-[#FF3B30] bg-[#FF3B30]/10' 
                          : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'
                      }`}
                    >
                      <type.icon className="w-5 h-5" />
                      <span className="text-xs">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Equipment Number</Label>
                <Input
                  value={formData.equipment_number}
                  onChange={(e) => setFormData({ ...formData, equipment_number: e.target.value })}
                  placeholder="e.g., H-001, SP-015"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="equipment-number-input"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Size (Optional)</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  placeholder="e.g., L, XL, 10.5"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="equipment-size-input"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Assign to Player (Optional)</Label>
                <Input
                  placeholder="Search player by name or jersey #..."
                  value={addPlayerSearch}
                  onChange={(e) => setAddPlayerSearch(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="add-player-search-input"
                />
                <div className="max-h-32 overflow-y-auto border border-[#27272A] bg-[#0A0A0A] mt-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, assigned_to: '' })}
                    className={`w-full flex items-center gap-3 p-2 text-left hover:bg-[#1A1A1A] transition-colors ${
                      formData.assigned_to === '' ? 'bg-[#FF3B30]/10 border-l-2 border-[#FF3B30]' : ''
                    }`}
                  >
                    <Package className="w-4 h-4 text-[#71717A]" />
                    <span className="text-[#A1A1AA] text-sm">No player (keep available)</span>
                  </button>
                  {filteredPlayersForAdd.map((player) => (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, assigned_to: player.id })}
                      className={`w-full flex items-center gap-3 p-2 text-left hover:bg-[#1A1A1A] transition-colors ${
                        formData.assigned_to === player.id ? 'bg-[#FF3B30]/10 border-l-2 border-[#FF3B30]' : ''
                      }`}
                      data-testid={`add-player-option-${player.id}`}
                    >
                      <User className="w-4 h-4 text-[#71717A]" />
                      <div>
                        <p className="text-white text-sm">{player.name}</p>
                        <p className="text-xs text-[#71717A]">#{player.jersey_number} • {player.position}</p>
                      </div>
                    </button>
                  ))}
                </div>
                {formData.assigned_to && (
                  <p className="text-xs text-[#32D74B] mt-1">
                    Equipment will be checked out to selected player
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAddModal(false)}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddEquipment}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                data-testid="save-equipment-btn"
              >
                Add Equipment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Equipment Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ ...editModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>Edit Equipment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Equipment Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {EQUIPMENT_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, equipment_type: type.value })}
                      className={`p-3 border flex flex-col items-center gap-1 transition-all ${
                        formData.equipment_type === type.value 
                          ? 'border-[#FF3B30] bg-[#FF3B30]/10' 
                          : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'
                      }`}
                    >
                      <type.icon className="w-5 h-5" />
                      <span className="text-xs">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Equipment Number</Label>
                <Input
                  value={formData.equipment_number}
                  onChange={(e) => setFormData({ ...formData, equipment_number: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Size</Label>
                <Input
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditModal({ open: false, item: null })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditEquipment}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ ...deleteModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle className="text-[#FF3B30]">Delete Equipment</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-[#A1A1AA]">
                Are you sure you want to delete <span className="text-white font-medium">
                  {deleteModal.item && `${getTypeLabel(deleteModal.item.equipment_type)} #${deleteModal.item.equipment_number}`}
                </span>?
              </p>
              <p className="text-sm text-[#71717A] mt-2">
                This action cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ open: false, item: null })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteEquipment}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Checkout Modal */}
        <Dialog open={checkoutModal.open} onOpenChange={(open) => {
          setCheckoutModal({ ...checkoutModal, open });
          if (!open) {
            setSelectedPlayerId('');
            setPlayerSearch('');
          }
        }}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>Check Out Equipment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-[#A1A1AA]">
                Checking out: <span className="text-white font-medium">
                  {checkoutModal.item && `${getTypeLabel(checkoutModal.item.equipment_type)} #${checkoutModal.item.equipment_number}`}
                </span>
              </p>
              
              <div>
                <Label className="text-[#A1A1AA]">Select Player</Label>
                <Input
                  placeholder="Search player by name or jersey #..."
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="player-search-input"
                />
              </div>
              
              <div className="max-h-48 overflow-y-auto border border-[#27272A] bg-[#0A0A0A]">
                {filteredPlayers.length > 0 ? (
                  filteredPlayers.map((player) => (
                    <button
                      key={player.id}
                      onClick={() => setSelectedPlayerId(player.id)}
                      className={`w-full flex items-center gap-3 p-3 text-left hover:bg-[#1A1A1A] transition-colors ${
                        selectedPlayerId === player.id ? 'bg-[#FF3B30]/10 border-l-2 border-[#FF3B30]' : ''
                      }`}
                      data-testid={`player-option-${player.id}`}
                    >
                      <User className="w-5 h-5 text-[#71717A]" />
                      <div>
                        <p className="text-white">{player.name}</p>
                        <p className="text-xs text-[#71717A]">#{player.jersey_number} • {player.position}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="p-3 text-[#71717A] text-center">No players found</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setCheckoutModal({ open: false, item: null });
                  setSelectedPlayerId('');
                  setPlayerSearch('');
                }}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCheckout}
                disabled={!selectedPlayerId}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white disabled:opacity-50"
                data-testid="confirm-checkout-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Check Out
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
